import { X, Eye, Heart, MessageCircle, Repeat2, TrendingUp, MapPin } from 'lucide-react'
import { useState, useEffect } from 'react'
import api from '../services/api'

interface PostAnalyticsModalProps {
  postId: string
  isOpen: boolean
  onClose: () => void
}

interface AnalyticsData {
  impressions: number
  reach: number
  uniqueViewers: number
  engagement: {
    likes: number
    comments: number
    reposts: number
    total: number
    rate: number
  }
  ripple: {
    status: string
    currentWave: number
    originRegion: string
    totalReach: number
    waves: Array<{
      waveNumber: number
      targetUsers: number
      actualReach: number
      engagementRate: string
      status: string
    }>
  }
  regions: Array<{
    region: string
    engagements: number
    percentage: string
  }>
}

export default function PostAnalyticsModal({ postId, isOpen, onClose }: PostAnalyticsModalProps) {
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && postId) {
      loadAnalytics()
    }
  }, [isOpen, postId])

  const loadAnalytics = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const headers = {
        'Content-Type': 'application/json',
        ...(localStorage.getItem('auth_token') ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } : {})
      }
      const res = await fetch(`https://api.onrecord.live/api/posts/${postId}/analytics`, { headers })
      if (!res.ok) {
        if (res.status === 403) {
          setError('Analytics are only available to the post owner')
        } else {
          setError('Failed to load analytics')
        }
        return
      }
      const result = await res.json()
      if (result.success) {
        setData(result.analytics)
      }
    } catch (err) {
      setError('Failed to load analytics')
    } finally {
      setIsLoading(false)
    }
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900">Post Analytics</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-onrecord-orange"></div>
          </div>
        )}

        {error && (
          <div className="p-8 text-center">
            <p className="text-gray-500">{error}</p>
          </div>
        )}

        {data && !isLoading && (
          <div className="p-4 space-y-4">
            {/* View Metrics */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-blue-50 rounded-xl p-3 text-center">
                <Eye className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                <p className="text-xl font-bold text-gray-900">{formatNumber(data.impressions)}</p>
                <p className="text-xs text-gray-500">Impressions</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-3 text-center">
                <TrendingUp className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                <p className="text-xl font-bold text-gray-900">{formatNumber(data.reach)}</p>
                <p className="text-xs text-gray-500">Reach</p>
              </div>
              <div className="bg-green-50 rounded-xl p-3 text-center">
                <Eye className="w-5 h-5 text-green-600 mx-auto mb-1" />
                <p className="text-xl font-bold text-gray-900">{formatNumber(data.uniqueViewers)}</p>
                <p className="text-xs text-gray-500">Unique</p>
              </div>
            </div>

            {/* Engagement */}
            <div className="bg-white border border-gray-100 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Engagement</h3>
                <span className="text-sm font-medium text-onrecord-orange">{data.engagement.rate}%</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-red-500" />
                  <div>
                    <p className="text-sm font-semibold">{formatNumber(data.engagement.likes)}</p>
                    <p className="text-xs text-gray-400">Likes</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-blue-500" />
                  <div>
                    <p className="text-sm font-semibold">{formatNumber(data.engagement.comments)}</p>
                    <p className="text-xs text-gray-400">Comments</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Repeat2 className="w-4 h-4 text-green-500" />
                  <div>
                    <p className="text-sm font-semibold">{formatNumber(data.engagement.reposts)}</p>
                    <p className="text-xs text-gray-400">Reposts</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ripple Status */}
            {data.ripple && data.ripple.waves.length > 0 && (
              <div className="bg-white border border-gray-100 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Ripple Waves</h3>
                <div className="space-y-2">
                  {data.ripple.waves.map((wave) => (
                    <div key={wave.waveNumber} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Wave {wave.waveNumber}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-500">{formatNumber(wave.actualReach)} reach</span>
                        <span className="text-onrecord-orange font-medium">{wave.engagementRate}%</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          wave.status === 'completed' ? 'bg-green-100 text-green-700' :
                          wave.status === 'active' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{wave.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Regions */}
            {data.regions && data.regions.length > 0 && (
              <div className="bg-white border border-gray-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <h3 className="font-semibold text-gray-900">Top Regions</h3>
                </div>
                <div className="space-y-2">
                  {data.regions.map((region, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-700">{region.region}</span>
                        <span className="text-gray-500">{region.percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div
                          className="bg-onrecord-orange h-full rounded-full"
                          style={{ width: `${parseFloat(region.percentage)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
